import { useState, useRef, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { PaperPlaneTilt, Robot, User, Sparkle, Clock, ChatCircleDots, Plus, Trash } from '@phosphor-icons/react';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';

const MODELS = [
  { id: 'auto', label: 'Auto', desc: 'Best model for the task' },
  { id: 'openai', label: 'GPT-5.2', desc: 'Complex reasoning' },
  { id: 'anthropic', label: 'Claude', desc: 'Explanations' },
  { id: 'gemini', label: 'Gemini', desc: 'Quick queries' },
];

export default function NLInterface() {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [selectedModel, setSelectedModel] = useState('auto');
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  useEffect(() => { scrollToBottom(); }, [messages]);

  const fetchSessions = useCallback(async () => {
    try {
      const res = await fetch(`${API}/nl/sessions`, { credentials: 'include' });
      if (res.ok) setSessions(await res.json());
    } catch (e) { /* silent */ }
  }, []);

  useEffect(() => { fetchSessions(); }, [fetchSessions]);

  const loadSession = async (sid) => {
    try {
      const res = await fetch(`${API}/nl/sessions/${sid}`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        const msgs = data.flatMap(m => [
          { role: 'user', content: m.query, timestamp: m.created_at },
          { role: 'assistant', content: m.response, model: m.model_used, processingTime: m.processing_time_ms, suggestions: m.suggestions || [], timestamp: m.created_at },
        ]);
        setMessages(msgs);
        setSessionId(sid);
      }
    } catch (e) { toast.error('Failed to load session'); }
  };

  const startNewSession = () => {
    setMessages([]);
    setSessionId(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!input.trim() || loading) return;

    const userMessage = { role: 'user', content: input, timestamp: new Date().toISOString() };
    setMessages(prev => [...prev, userMessage]);
    const currentInput = input;
    setInput('');
    setLoading(true);

    try {
      const body = {
        query: currentInput,
        session_id: sessionId,
        preferred_model: selectedModel === 'auto' ? null : selectedModel,
      };

      const response = await fetch(`${API}/nl/query`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(body),
      });

      if (response.ok) {
        const data = await response.json();
        if (!sessionId && data.session_id) setSessionId(data.session_id);
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: data.interpretation,
          model: data.model_used,
          processingTime: data.processing_time_ms,
          suggestions: data.suggestions || [],
          timestamp: new Date().toISOString(),
        }]);
        fetchSessions();
      } else {
        toast.error('Failed to process query');
        setMessages(prev => [...prev, { role: 'assistant', content: 'Sorry, I encountered an error processing your request.', timestamp: new Date().toISOString(), suggestions: [] }]);
      }
    } catch (error) {
      toast.error('Connection error');
      setMessages(prev => [...prev, { role: 'assistant', content: 'Sorry, I could not connect to the server.', timestamp: new Date().toISOString(), suggestions: [] }]);
    } finally {
      setLoading(false);
    }
  };

  const handleSuggestion = (suggestion) => {
    setInput(suggestion);
  };

  const suggestions = [
    'Show me my API connections and their health',
    'What errors occurred in the last 24 hours?',
    'Help me create a data transform for Stripe to Uday',
    'Analyze my recent job executions',
  ];

  return (
    <div className="h-[calc(100vh-140px)] flex gap-4" data-testid="nl-interface-page">
      {/* Sessions sidebar */}
      <div className="w-56 flex-shrink-0 flex flex-col card-brutal overflow-hidden" data-testid="nl-sessions-sidebar">
        <div className="p-3 border-b border-kova-cyan/12 flex items-center justify-between">
          <span className="text-[10px] font-mono text-kova-slate uppercase tracking-wider">Conversations</span>
          <button
            onClick={startNewSession}
            className="text-kova-cyan hover:text-kova-cyan/80"
            title="New conversation"
            data-testid="new-session-btn"
          >
            <Plus size={14} weight="bold" />
          </button>
        </div>
        <div className="flex-1 overflow-auto">
          {sessions.length === 0 ? (
            <p className="p-3 text-[10px] text-kova-slate font-mono">No conversations yet</p>
          ) : (
            sessions.map(s => (
              <button
                key={s.session_id}
                onClick={() => loadSession(s.session_id)}
                className={`w-full text-left px-3 py-2.5 border-b border-kova-card2 hover:bg-kova-card/50 transition-colors ${sessionId === s.session_id ? 'bg-kova-cyan/8 border-l-2 border-l-kova-cyan' : ''}`}
                data-testid={`session-${s.session_id}`}
              >
                <div className="text-xs text-kova-silv truncate">{s.title?.slice(0, 40) || 'Untitled'}</div>
                <div className="flex items-center gap-2 mt-0.5">
                  <span className="text-[9px] text-kova-slate font-mono">{s.message_count} msg{s.message_count !== 1 ? 's' : ''}</span>
                  {s.model_used && <span className="text-[9px] text-kova-cyan/60 font-mono">{s.model_used.split('/')[0]}</span>}
                </div>
              </button>
            ))
          )}
        </div>
      </div>

      {/* Chat area */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Header */}
        <div className="flex items-center justify-between mb-3">
          <div>
            <h1 className="font-heading font-bold text-lg flex items-center gap-2">
              <Sparkle size={20} className="text-kova-cyan" weight="fill" />
              Natural Language Query
            </h1>
            <p className="text-[10px] text-kova-slate font-mono mt-0.5">Powered by multi-LLM routing · context-aware</p>
          </div>
          {/* Model selector */}
          <div className="flex items-center gap-1" data-testid="model-selector">
            {MODELS.map(m => (
              <button
                key={m.id}
                onClick={() => setSelectedModel(m.id)}
                className={`px-2.5 py-1 text-[9px] font-mono uppercase tracking-wider border transition-colors ${
                  selectedModel === m.id
                    ? 'bg-kova-cyan/15 text-kova-cyan border-kova-cyan/30'
                    : 'bg-transparent text-kova-slate border-kova-cyan/12 hover:text-kova-silv'
                }`}
                title={m.desc}
                data-testid={`model-${m.id}`}
              >
                {m.label}
              </button>
            ))}
          </div>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-auto card-brutal p-4 mb-3">
          {messages.length === 0 ? (
            <div className="h-full flex flex-col items-center justify-center text-center">
              <ChatCircleDots size={48} className="text-kova-cyan/12 mb-4" />
              <h3 className="font-heading font-bold text-base mb-2">Ask KOVA anything</h3>
              <p className="text-kova-slate text-xs mb-6 max-w-md font-mono">Connections, logs, transforms, alerts, scheduling — I have full context of your KOVA workspace.</p>
              <div className="flex flex-wrap gap-2 justify-center max-w-lg">
                {suggestions.map((s, i) => (
                  <button
                    key={i}
                    onClick={() => setInput(s)}
                    className="px-3 py-2 text-[11px] font-mono bg-kova-card2 border border-kova-cyan/12 hover:border-kova-cyan/40 hover:text-kova-cyan transition-colors text-kova-silv"
                    data-testid={`suggestion-${i}`}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              {messages.map((msg, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className={`flex gap-3 ${msg.role === 'user' ? 'justify-end' : ''}`}
                >
                  {msg.role === 'assistant' && (
                    <div className="w-7 h-7 bg-kova-cyan/10 flex items-center justify-center flex-shrink-0">
                      <Robot size={16} className="text-kova-cyan" />
                    </div>
                  )}
                  <div className={`max-w-[80%] ${msg.role === 'user' ? 'bg-kova-cyan/10 border-kova-cyan/30' : 'bg-kova-card2 border-kova-cyan/12'} border p-3`}>
                    <p className="text-sm whitespace-pre-wrap leading-relaxed">{msg.content}</p>
                    {msg.model && (
                      <div className="flex items-center gap-3 mt-2 pt-2 border-t border-kova-cyan/12 text-[10px] text-kova-slate">
                        <span className="font-mono">{msg.model}</span>
                        <span className="flex items-center gap-1"><Clock size={10} />{msg.processingTime}ms</span>
                      </div>
                    )}
                    {/* Suggestions */}
                    {msg.role === 'assistant' && msg.suggestions?.length > 0 && index === messages.length - 1 && (
                      <div className="flex flex-wrap gap-1.5 mt-2 pt-2 border-t border-kova-cyan/12">
                        {msg.suggestions.map((s, si) => (
                          <button
                            key={si}
                            onClick={() => handleSuggestion(s)}
                            className="text-[10px] font-mono px-2 py-1 bg-kova-cyan/8 border border-kova-cyan/20 text-kova-cyan hover:bg-kova-cyan/15 transition-colors"
                            data-testid={`followup-${si}`}
                          >
                            {s}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  {msg.role === 'user' && (
                    <div className="w-7 h-7 bg-kova-cyan/12 flex items-center justify-center flex-shrink-0">
                      <User size={16} className="text-kova-silv" />
                    </div>
                  )}
                </motion.div>
              ))}
              {loading && (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex gap-3">
                  <div className="w-7 h-7 bg-kova-cyan/10 flex items-center justify-center flex-shrink-0">
                    <Robot size={16} className="text-kova-cyan" />
                  </div>
                  <div className="bg-kova-card2 border border-kova-cyan/12 p-3">
                    <span className="font-mono text-xs text-kova-cyan">Processing with {MODELS.find(m => m.id === selectedModel)?.label || 'Auto'}...</span>
                  </div>
                </motion.div>
              )}
              <div ref={messagesEndRef} />
            </div>
          )}
        </div>

        {/* Input */}
        <form onSubmit={handleSubmit} className="flex gap-2">
          <Textarea
            data-testid="nl-query-input"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSubmit(e); } }}
            placeholder="Describe what you want, ask about your integrations..."
            className="flex-1 bg-kova-bg border-kova-cyan/12 focus:border-kova-cyan rounded-none min-h-[48px] max-h-[100px] resize-none text-sm"
            disabled={loading}
          />
          <Button
            type="submit"
            data-testid="nl-submit-button"
            disabled={loading || !input.trim()}
            className="bg-kova-cyan text-kova-bg hover:bg-kova-cyan/90 rounded-none px-5 self-end"
          >
            <PaperPlaneTilt size={16} weight="fill" />
          </Button>
        </form>
      </div>
    </div>
  );
}
